import qs from 'qs';
import { SAFEA_V3_ENABLED } from '~/constants/_core/safea-v3';
import { getImpersonateData, removeImpersonateData, saveImpersonateData } from '~/helpers/_core/impersonate';
import {
  buildAuthorizeUrl,
  buildSignOutUrl,
  getUserType
} from '~/helpers/_core/safea-v3';
import storage, { STORAGE_TYPES } from '~/helpers/_core/storage';

const _signoutRedirectUrl = {
  get: ($cookiz) => {
    if (process.client) {
      return storage.local.signoutRedirectUrl;
    }
    return $cookiz.get(STORAGE_TYPES.SIGNOUT_REDIRECT_URL, { fromRes: false });
  },
  set: ($cookiz, signoutRedirectUrl) => {
    if (process.client) {
      storage.local.signoutRedirectUrl = signoutRedirectUrl;
    }
    $cookiz.set(STORAGE_TYPES.SIGNOUT_REDIRECT_URL, signoutRedirectUrl);
  },
  remove: ($cookiz) => {
    if (process.client) {
      storage.local.signoutRedirectUrl = null;
    }
    $cookiz.remove(STORAGE_TYPES.SIGNOUT_REDIRECT_URL);
  }
};

const _handleEachIes = (ies, applicationAlias) => {
  const iesRights = [];
  ies.forEach(ies => {
    const iesRolesOnApplication = ies.applications.find(a => a.alias.toLocaleUpperCase() === applicationAlias);
    if (iesRolesOnApplication?.roles?.length) {
      const iesRight = {
        id                   : ies.id,
        name                 : ies.name,
        alias                : ies.alias,
        uuid                 : ies.uuid,
        hostname             : ies.hostname,
        iesLogoMini          : ies.iesLogoMini,
        iesLogoMiniThemeDark : ies.iesLogoMiniThemeDark,
        roles                : iesRolesOnApplication.roles
      };
      iesRights.push(iesRight);
    }
  });
  return iesRights;
};

const buildTenantsRights = (rootState, commit, result) => {
  const owner = rootState._core.application.owner;
  const applicationAlias = rootState._core.application.alias.toLocaleUpperCase();
  const tenantsRights = [];
  let tenants = result.data.tenants || [result.data.tenant];

  if (owner) {
    // TODO: remove after fully migrating to v3
    tenants = tenants.filter(e => e.alias === rootState._core.tenant.alias || e.id === rootState._core.tenant.id);
  }

  tenants.forEach(tenant => {
    let tenantRolesOnApplication;
    if (owner === 'ies') {
      // TODO: remove after fully migrating to v3
      tenant.ies = tenant.ies.filter(e => e.alias === rootState._core.tenant.iesAlias || e.id === rootState._core.tenant.institution.id);
    } else {
      tenantRolesOnApplication = tenant.applications.find(a => a.alias.toLocaleUpperCase() === applicationAlias);
    }

    const iesRights = _handleEachIes(tenant.ies, applicationAlias);

    if (tenantRolesOnApplication || iesRights.length) {
      const tenantRights = {
        id                      : tenant.id,
        alias                   : tenant.alias,
        uuid                    : tenant.uuid,
        name                    : tenant.name,
        hostname                : tenant.hostname,
        tenantLogoMini          : tenant.tenantLogoMini,
        tenantLogoMiniThemeDark : tenant.tenantLogoMiniThemeDark,
        roles                   : tenantRolesOnApplication ? tenantRolesOnApplication.roles : [],
        ies                     : iesRights || []
      };

      tenantsRights.push(tenantRights);
    }
  });

  let applicationsRoles = {};

  if (result.data.applications) {
    applicationsRoles = result.data.applications.find(item => item.alias.toLocaleUpperCase() === applicationAlias);
  }

  commit('SET_TENANT_RIGHTS', tenantsRights);
  commit('SET_ROLES_RIGHTS', applicationsRoles);
};

const buildUserAvailableApplications = (ctx, result) => {
  const { commit } = ctx;

  if (result.data.user.type === 'userTenant') {
    return commit(
      '_core/user/SET_USER_AVAILABLE_APPLICATIONS',
      {
        applications : [],
        tenants      : [result.data.tenant]
      },
      { root: true }
    );
  }

  return commit(
    '_core/user/SET_USER_AVAILABLE_APPLICATIONS',
    {
      applications : result.data.applications,
      tenants      : result.data.tenants
    },
    { root: true }
  );
};

const buildUserApplicationSettings = (app, result) => {
  app.vuetify.framework.theme.dark = process.env.DARK_MODE_ENABLED ? result.data.user.accessibility.darkThemeEnabled : false;
  app.i18n.locale = result.data.user.languageId;
};

const processCurrentRole = (currentRole, tenantsRights, applicationsRoles) => {
  let validRole = {
    id    : null,
    name  : null,
    alias : null
  };

  if (currentRole.tenantAlias) {
    const tenant = tenantsRights.find(tenant => tenant.alias === currentRole.tenantAlias);
    if (tenant) {
      if (currentRole.iesAlias) {
        const ies = tenant.ies.find(ies => ies.alias === currentRole.iesAlias);

        if (ies) {
          validRole = ies.roles.find(role => role.roleId === currentRole.roleId);
        }
      } else {
        validRole = tenant.roles.find(role => role.roleId === currentRole.roleId);
      }
    }
  } else {
    validRole = applicationsRoles.roles.find(role => role.roleId === currentRole.roleId);
  }

  return {
    tenant: {
      name     : currentRole.tenantName,
      alias    : currentRole.tenantAlias,
      uuid     : currentRole.tenantUuid,
      id       : currentRole.tenantId,
      hostname : currentRole.tenantHostname
    },
    ies: {
      name     : currentRole.iesName,
      alias    : currentRole.iesAlias,
      uuid     : currentRole.iesUuid,
      id       : currentRole.iesId,
      hostname : currentRole.iesHostname
    },
    role: {
      name   : validRole.name,
      alias  : validRole.alias,
      id     : validRole.id,
      roleId : validRole.roleId
    }
  };
};

export default {
  init({ dispatch, commit, rootState }) {
    if (this.$token.get()) {
      let signoutRedirectUrl = _signoutRedirectUrl.get(this.app.$cookiz);

      if (!signoutRedirectUrl) {
        if (process.client) {
          const searchParams = new URLSearchParams(window.location.search);
          signoutRedirectUrl = searchParams.get('signoutRedirectUrl');
        } else {
          signoutRedirectUrl = this.$router.currentRoute.query.signoutRedirectUrl;
        }

        if (signoutRedirectUrl) {
          _signoutRedirectUrl.set(this.app.$cookiz, signoutRedirectUrl);
        }
      }

      return this.$services._core.user().getUserInfo()
        .then((result) => {
          return dispatch('processSignIn', result);
        })
        .catch((error) => {
          commit('SET_TOKEN', null);

          console.error('auth/init - signIn', error);

          return error;
        });
    }
  },

  async updateRightsAndRoles({ commit, rootState, state }, result) {
    buildTenantsRights(rootState, commit, result);

    const currentRole = processCurrentRole(result.data.currentRole, state.tenantsRights, state.applicationsRoles);
    commit('SET_CURRENT_ROLE', currentRole);

    return currentRole;
  },

  async processSignIn(ctx, result) {
    const { rootState, state, dispatch, commit } = ctx;
    buildTenantsRights(rootState, commit, result);
    buildUserAvailableApplications(ctx, result);
    if (!SAFEA_V3_ENABLED) {
      buildUserApplicationSettings(this.app, result);
      commit('_core/locales/SET_LANG', result.data.user.languageId, { root: true });
      commit('_core/user/SET_USER', result.data.user, { root: true });

      if (result.data.accessToken) {
        this.$token.set(result.data.accessToken);
      }
    }
    commit('SET_LAST_ACCESS', result.data.lastAccess ?? null);

    if (process.client) {
      this.$sentry.setUser(
        result.data.user
      );
    }

    if (result.data.features) {
      commit('_core/application/SET_FEATURES', result.data.features, { root: true });
    }

    if (result.data.currentRole) {
      const currentRole = processCurrentRole(result.data.currentRole, state.tenantsRights, state.applicationsRoles);
      commit('SET_CURRENT_ROLE', currentRole);

      if (result.data.impersonatedByUser) {
        commit('SET_IMPERSONATED_BY', result.data.impersonatedByUser);
      }

      await dispatch('_core/tenant/changeTheme', currentRole, { root: true });
    } else if (result.data.lastAccess && !SAFEA_V3_ENABLED) {
      const lastAccessRights = processCurrentRole(result.data.lastAccess, state.tenantsRights, state.applicationsRoles);
      await dispatch('actionAssumeRole', lastAccessRights);
    }

    commit('SET_AUTH_MODE', result.data.authMode);

    return result;
  },

  redirectToSignOutUrl({ rootState, dispatch }, { redirect, route } = {}) {
    const signoutRedirectUrl = _signoutRedirectUrl.get(this.$cookiz);

    dispatch('actionSignOutCleanup');

    if (signoutRedirectUrl) {
      _signoutRedirectUrl.remove(this.$cookiz);

      if ((/^http?:\/\//i).test(signoutRedirectUrl) || (/^https?:\/\//i).test(signoutRedirectUrl)) {
        if (typeof redirect === 'function') {
          return redirect(signoutRedirectUrl);
        }
        window.location.replace(signoutRedirectUrl);
      }
    } else {
      if (typeof redirect === 'function') {
        const fullPath = Object.keys(route.query).length > 0 ? `${route.path}?${qs.stringify(route.query)}` : route.path;
        storage.session.returnUrl = fullPath;
        return redirect('/auth/signin', {
          signoutRedirectUrl: route.query.signoutRedirectUrl
        });
      }
      // return this.$router.push('/auth/signin');
      // page reload done with window.location.href for equalWeb to work correctly on Plataforma A
      window.location.href = window.origin + '/plataforma';
    }
  },

  async actionSignIn(ctx, { username, password, captcha }) {
    const { rootState, state, dispatch, commit } = ctx;
    const bodyPost = {
      captcha,
      username,
      password,
      applicationAlias : rootState._core.application.alias,
      iesAlias         : rootState._core.tenant.iesAlias || null
    };

    const result = await this.$services._core.auth().signIn(bodyPost, rootState._core.tenant.alias || null);

    this.$token.set(result.data.accessToken);

    buildTenantsRights(rootState, commit, result);
    buildUserAvailableApplications(ctx, result);
    buildUserApplicationSettings(this.app, result);

    commit('_core/locales/SET_LANG', result.data.user.languageId, { root: true });
    commit('_core/user/SET_USER', result.data.user, { root: true });
    commit('SET_LAST_ACCESS', result.data.lastAccess ?? null);

    if (result.data.user.temporaryPassword) {
      commit('_core/user/SET_USER_PASSWORD', password, { root: true });
    }

    if (result.data.lastAccess) {
      const lastAccessRights = processCurrentRole(result.data.lastAccess, state.tenantsRights, state.applicationsRoles);

      await dispatch('actionAssumeRole', lastAccessRights);
    }

    commit('SET_AUTH_MODE', 'signin');

    return result;
  },

  async actionSSOUrl({ rootState }, applicationAlias) {
    const bodyPost = {
      uuid        : rootState._core.user.uuid,
      roleAlias   : rootState._core.auth.currentRole.role.alias,
      tenantAlias : rootState._core.auth.currentRole.tenant.alias,
      iesAlias    : rootState._core.auth.currentRole.ies.alias
    };

    const { data: { redirectUrl } } = await this.$services._core.auth().signinSSOUrl(bodyPost, applicationAlias);

    return redirectUrl;
  },

  async actionSignInImpersonate({ rootState }, {
    username,
    userId,
    roleAlias,
    redirect = window.location.href
  }) {
    if (SAFEA_V3_ENABLED) {
      const { refreshToken } = this.$token.getTokens();
      const result = await this.$services._core.authV3().impersonate({
        refreshToken,
        username,
        userId,
        roleAlias
      });

      this.$token.setTokens({
        ...this.$token.getTokens(),
        accessToken  : `${result.token_type} ${result.access_token}`,
        refreshToken : result.refresh_token
      });

      saveImpersonateData({
        store   : this,
        payload : {
          originPath : '/my-account',
          redirect   : redirect ?? '/',
          userId     : userId,
          username   : username,
          roleAlias  : roleAlias
        }
      });
    } else {
      const bodyPost = {
        username,
        roleAlias
      };

      const result = await this.$services._core.auth().impersonate(bodyPost);

      this.$token.set(result.data.accessToken);

      saveImpersonateData({
        store   : this,
        payload : {
          originPath : this.$router.currentRoute.path,
          redirect   : redirect ?? '/'
        }
      });
    }

    // TODO: eliminate this reload and just populate the store again?
    window.location.href = `${process.env.APPLICATION_ROUTE_BASE}${redirect}`;
  },

  async actionSignOutImpersonate({ rootState }) {
    const impersonate = getImpersonateData({ store: this });
    if (SAFEA_V3_ENABLED) {
      const { refreshToken } = this.$token.getTokens();
      const data = await this.$services._core.oauthV3().refreshToken({ refreshToken });

      await this.$token.setTokens({
        idToken      : data.id_token,
        refreshToken : data.refresh_token,
        accessToken  : `${data.token_type} ${data.access_token}`
      });
    } else {
      // If "usedAccessToken" is the same as the localStorage one (which happens when the user refreshes another tab)
      // it won't update session storage. So we just force it to update anyway.
      // If we don't do that, the user will be stuck in the impersonated state.
      this.$token.set(rootState._core.auth.impersonatedBy.usedAccessToken);
      this.$session.set(rootState._core.auth.impersonatedBy.usedAccessToken, false);
    }

    removeImpersonateData({ store: this });
    // TODO: eliminate this reload and just populate the store again?
    window.location.href = `${process.env.APPLICATION_ROUTE_BASE}${impersonate ? impersonate.originPath : '/' }`;
  },

  async actionSignUp({ rootState, dispatch }, {
    uuid,
    name,
    username,
    email,
    password,
    cellphone,
    captcha
  }) {
    const urlPath = this.getters['_core/auth/getCurrentRoleHostname'];

    const bodyPost = {
      captcha,
      uuid,
      name,
      username,
      email,
      password,
      cellphone,
      applicationAlias   : rootState._core.application.alias,
      firstAccessPath    : encodeURIComponent(`${urlPath}/auth/password/new`),
      applicationLogoUrl : encodeURIComponent(rootState._core.application.logo.light)
    };

    await this.$services._core.user().signUp(bodyPost, rootState._core.tenant.alias, rootState._core.tenant.iesAlias || null);
  },

  async actionAssumeRole({ rootState, commit, dispatch }, { tenant, ies, role }) {
    const applicationAlias = rootState._core.application.alias;
    const bodyPost = {
      roleAlias: role.alias,
      applicationAlias
    };

    const lastAccess = {
      tenantAlias : tenant?.alias || null,
      iesAlias    : ies?.alias || null,
      roleAlias   : role.alias,
      applicationAlias,
      occurred    : new Date()
    };

    let result;
    try {
      result = await this.$services._core.auth().assumeRole(tenant?.alias || null, ies?.alias || null, bodyPost);
    } catch (err) {
      console.error('[Cockpit] Failed to assume role', err);
      return false;
    }

    const { data } = result;
    this.$token.set(data.accessToken);

    await dispatch('_core/tenant/changeTheme', { tenant, ies, role }, { root: true });

    commit('SET_WEBCOMPONENT', []);
    commit('SET_LAST_ACCESS', lastAccess);
    commit('_core/application/SET_FEATURES', data.features, { root: true });

    const currentRole = {
      tenant: {
        name     : data.tenant?.name || null,
        alias    : data.tenant?.alias || null,
        uuid     : data.tenant?.uuid || null,
        id       : data.tenant?.id || null,
        hostname : data.tenant?.hostname || null
      },
      ies: {
        name     : data.ies?.name || null,
        alias    : data.ies?.alias || null,
        uuid     : data.ies?.uuid || null,
        id       : data.ies?.id || null,
        hostname : data.ies?.hostname || null
      },
      role: {
        name   : data.role.name,
        alias  : data.role.alias,
        id     : data.role.id,
        roleId : data.role.roleId
      }
    };

    commit('SET_CURRENT_ROLE', currentRole);
    return result;
  },

  async actionSignOut({ rootState, commit, dispatch }) {
    let samlLogoutRequest = null;
    if (!SAFEA_V3_ENABLED && rootState._core.application.authenticationsModes.hasAuthBySaml && this.$token.get()) {
      const { data: { samlLogoutRequest : redirectUrl } } = await this.$services._core.auth().signOutSaml();

      samlLogoutRequest = redirectUrl;
    }

    const { idToken } = this.$token.getTokens() ?? {};

    this.app.context.$vuetify.theme.dark = false;
    this.$handTalk.start(false);
    this.$token.clear();

    if (SAFEA_V3_ENABLED) {
      return window.location.replace(buildSignOutUrl({
        id_token_hint            : idToken,
        user_type                : getUserType(rootState._core.application.owner),
        client_id                : process.env.SAFEA_CLIENT_ID,
        tenant_uuid              : rootState._core.tenant.uuid,
        institution_uuid         : rootState._core.tenant.institution.uuid,
        post_logout_redirect_uri : process.env.SAFEA_POST_LOGOUT_REDIRECT_URI
      }));
    }

    if (samlLogoutRequest) {
      return window.location.replace(samlLogoutRequest);
    }

    if (SAFEA_V3_ENABLED) {
      await dispatch('_core/application/init', commit, { root: true });
    } else {
      await dispatch('_core/tenant/init', commit, { root: true });
    }
    commit('_core/user/RESET_USER_ACCESSIBILITY', null, { root: true });

    dispatch('redirectToSignOutUrl');
  },

  actionSignOutCleanup({ rootState, commit }) {
    if (rootState._core.application.owner !== 'ies') {
      commit('_core/tenant/RESET_IES', null, { root: true });
    }
    commit('SIGNOUT');
  },

  actionRecoverUsername({ rootState }, { email, captcha }) {
    const bodyPost = {
      captcha,
      email,
      applicationLogoUrl: encodeURIComponent(rootState._core.application.logo.light)
    };

    return this.$services._core.user().recoverUsername(bodyPost)
      .then(result => {
        return result.data;
      });
  },

  actionResetUserPassword({ rootState }, { user, username, captcha }) {
    const urlPath = this.getters['_core/auth/getCurrentRoleHostname'];

    const bodyPost = {
      captcha,
      user                 : user || username,
      applicationLogoUrl   : encodeURIComponent(rootState._core.application.logo.light),
      resetApplicationPath : encodeURIComponent(`${urlPath}/auth/password/change/`)
    };

    return this.$services._core.user().resetPassword(bodyPost)
      .then(result => {
        return result.data;
      });
  },

  actionResetUserPasswordConfirmed({ rootState }, { newPassword, token, captcha }) {
    const urlPath = this.getters['_core/auth/getCurrentRoleHostname'];

    const bodyPost = {
      captcha,
      newPassword,
      token,
      applicationLogoUrl   : encodeURIComponent(rootState._core.application.logo.light),
      resetApplicationPath : encodeURIComponent(urlPath)
    };

    return this.$services._core.user().resetPasswordConfirmed(bodyPost)
      .then(result => {
        return result.data;
      });
  },

  // v3
  async actionAssumeRoleV3({ rootState, redirect }, { tenantUuid, institutionUuid, v2RoleId }) {
    const authorizeUrl = await buildAuthorizeUrl({
      clientId        : process.env.SAFEA_CLIENT_ID,
      owner           : rootState._core.application.owner,
      hostname        : window.location.hostname,
      returnUrl       : this.$router.currentRoute.fullPath,
      tenantUuid      : tenantUuid ?? null,
      institutionUuid : institutionUuid ?? null,
      v2RoleId
    });

    window.location.assign(authorizeUrl);
  }
};
